// Extension batch UI — Import Cases, the Run modal, the live Running grid, and Settings
// (git repo token + Sample-File Kit). Mirrors the exe's ExeRunner batch tabs but talks to
// the /extension/* endpoints. See Docs/extension-batch-and-git-access-plan.md.
import { useEffect, useRef, useState } from "react";
import {
  Upload, FileSpreadsheet, Loader2, Play, Trash2, Pause, Square, RotateCw,
  GitBranch, Paperclip, CheckSquare, Square as SquareIcon, AlertTriangle,
  Cpu, DollarSign, Clock, LogIn, Ban, Wifi, WifiOff, Download,
} from "lucide-react";
import EmptyState from "@/components/shared/EmptyState";
import Modal from "@/components/dashboard/Modal";
import RerunBatchButton from "@/components/dashboard/RerunBatchButton";
import DevStatusCell from "@/components/dashboard/DevStatusCell";
import ImportTestCasesPanel from "@/components/dashboard/ImportTestCasesPanel";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { StatusBadge } from "@/components/ui/Badge";
import {
  detectExtColumns, importExtCases, listExtCases, deleteExtCase, createExtBatch,
  listExtBatches, getExtBatch, getExtBatchItems, pauseExtBatch,
  resumeExtBatch, stopExtBatch, takeoverExtBatch, recompileExtBatch,
  retryExtItem, cancelExtItem, downloadExtBatchReport,
  getExtRerunPreview, rerunExtBatch,
  listExtRepoConfigs, createExtRepoConfig,
  listExtSampleFiles, uploadExtSampleFile, deleteExtSampleFile,
  deleteAllExtCases, deleteAllExtBatches, clearAllExtData,
} from "@/api/extension";
import DeleteAllButton from "@/components/shared/DeleteAllButton";
import { getProjects } from "@/api/projects";
import { toastError } from "@/lib/toast";

// Short "org/repo" label from a git URL, for the repo picker + list.
function repoLabel(url) {
  const m = String(url || "").replace(/\.git$/, "").match(/[:/]([^/]+\/[^/]+)$/);
  return m ? m[1] : (url || "repo");
}

const selectCls =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200";

// Column mapping config for the extension import — each field carries its own
// rule-based header aliases (free guess). AI auto-detect is the fallback for
// cryptic headers. Consumed by the shared <ImportTestCasesPanel>.
const EXT_FIELDS = [
  { key: "name", label: "Name / title", aliases: ["name", "title", "test name", "test case", "testcase", "case"] },
  { key: "url", label: "Website URL", aliases: ["url", "link", "website", "site", "page", "endpoint"] },
  { key: "instruction", label: "Steps / instruction", aliases: ["instruction", "steps", "step", "action", "actions", "procedure", "test steps", "steps to reproduce", "reproduce", "repro", "reproduction", "how to reproduce"] },
  { key: "expected_result", label: "Expected result", aliases: ["expected", "expected result", "result", "expectation"] },
  { key: "test_scenario", label: "Scenario", aliases: ["scenario", "test scenario"] },
  { key: "test_description", label: "Description", aliases: ["description", "desc", "details"] },
  { key: "preconditions", label: "Preconditions", aliases: ["precondition", "preconditions", "prerequisite", "setup"] },
  { key: "test_data", label: "Test data", aliases: ["data", "test data", "input", "inputs"] },
  { key: "external_id", label: "External ID", aliases: ["id", "external id", "case id", "tc id", "ref", "number"] },
  { key: "category", label: "Module / category", aliases: ["module", "category", "type", "feature", "area", "component"] },
  { key: "attachment", label: "Attachment file", aliases: ["attachment", "file", "upload", "document", "attach"] },
  { key: "login_username", label: "Login username / email", aliases: ["login username", "login email", "login user", "login id", "username", "user"] },
  { key: "login_password", label: "Login password", aliases: ["login password", "password", "pass", "pwd"] },
  { key: "credentials", label: "Credentials (combined)", aliases: ["credentials", "credential", "login", "logins", "account", "gmail", "auth", "user credentials"] },
];

// =========================================================================== //
//  IMPORT CASES
// =========================================================================== //
export function ImportCasesTab({ onRun }) {
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [projects, setProjects] = useState([]);

  const loadCases = async () => {
    try { setCases(await listExtCases(500)); } catch (e) { setCases([]); }
  };
  useEffect(() => { loadCases(); }, []);
  // The project list drives the picker on the import panel. A failure here must
  // not block importing — the picker simply falls back to "no project".
  useEffect(() => {
    getProjects().then((p) => setProjects(Array.isArray(p) ? p : [])).catch(() => setProjects([]));
  }, []);

  // Shape the shared panel's mapped rows into the extension's import payload.
  const submitExt = (rows, { fileName, defaultUrl, projectId }) =>
    importExtCases({
      cases: rows.map((r) => ({
        external_id: r.external_id, name: r.name, url: r.url,
        instruction: r.instruction, expected_result: r.expected_result,
        test_scenario: r.test_scenario, test_description: r.test_description,
        preconditions: r.preconditions, test_data: r.test_data,
        category: r.category, attachment: r.attachment,
        login_username: r.login_username, login_password: r.login_password,
        credentials: r.credentials,
      })),
      imported_from: fileName, default_url: defaultUrl || null,
      project_id: projectId || null,
    });

  const allSel = cases.length > 0 && selected.size === cases.length;
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(cases.map((c) => c.id)));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function removeCase(id) {
    try { await deleteExtCase(id); setSelected((s) => { const n = new Set(s); n.delete(id); return n; }); await loadCases(); } catch (x) { /* ignore */ }
  }

  return (
    <div className="space-y-5">
      {/* upload + mapping */}
      <ImportTestCasesPanel
        fields={EXT_FIELDS}
        showDefaultUrl
        projects={projects}
        requireDetect
        onDetect={(cols, samples) => detectExtColumns(cols, samples)}
        onSubmit={submitExt}
        onImported={loadCases}
      />

      {/* imported catalog + run */}
      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-800 dark:text-white">Imported cases ({cases.length})</p>
          <div className="flex items-center gap-2">
            <button onClick={toggleAll} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400">
              {allSel ? <CheckSquare size={14} /> : <SquareIcon size={14} />} {allSel ? "Deselect all" : "Select all"}
            </button>
            <DeleteAllButton
              label="Delete all"
              title="Delete all imported cases?"
              message={`Permanently delete all ${cases.length} imported case${cases.length === 1 ? "" : "s"} in your workspace. Past run history is kept.`}
              onDelete={deleteAllExtCases}
              onDone={loadCases}
              disabled={cases.length === 0}
            />
            <Button size="sm" disabled={!selected.size} onClick={() => onRun([...selected])}>
              <Play size={14} /> Run {selected.size || ""} test case{selected.size === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
        {cases.length === 0 ? (
          <EmptyState icon={FileSpreadsheet} title="No imported cases yet" message="Upload an .xlsx above to add test cases." />
        ) : (
          <ul className="max-h-[420px] space-y-1.5 overflow-y-auto">
            {cases.map((c) => (
              <li key={c.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-2.5 dark:border-white/5 dark:bg-white/5">
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="h-4 w-4 shrink-0 accent-brand-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{c.name}</p>
                  <p className="truncate text-xs text-slate-400">{(c.url || "").replace(/^https?:\/\//, "")}{c.attachment ? ` · 📎 ${c.attachment}` : ""}</p>
                </div>
                {c.replayable && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.625rem] font-semibold text-emerald-600 dark:text-emerald-300">replay</span>}
                {c.ran && <StatusBadge status={c.last_status} />}
                <button onClick={() => removeCase(c.id)} className="text-slate-400 hover:text-rose-500"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// =========================================================================== //
//  RUN MODAL
// =========================================================================== //
export function RunBatchModal({ caseIds, devices, onClose, onStarted }) {
  const online = devices.filter((d) => d.status === "online");
  const [picked, setPicked] = useState(() => new Set(online.map((d) => d.id)));
  const [name, setName] = useState("");
  const [batchSize, setBatchSize] = useState(10);
  const [maxCost, setMaxCost] = useState("");
  const [autoCompile, setAutoCompile] = useState(true);
  const [useRepo, setUseRepo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Saved repos (per project) — pick which one to pre-compile from, or add one inline.
  const [repos, setRepos] = useState([]);
  const [repoId, setRepoId] = useState("");
  const [adding, setAdding] = useState(false);
  const [newRepo, setNewRepo] = useState({ repo_url: "", branch: "", token: "" });
  const [savingRepo, setSavingRepo] = useState(false);

  const loadRepos = async () => {
    try {
      const list = await listExtRepoConfigs();
      setRepos(list);
      if (list.length && !repoId) setRepoId(list[0].id);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadRepos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function addRepo() {
    if (!newRepo.repo_url.trim()) return;
    setSavingRepo(true); setErr(null);
    try {
      const created = await createExtRepoConfig({
        repo_url: newRepo.repo_url.trim(), branch: newRepo.branch.trim() || null,
        token: newRepo.token || null,
      });
      setNewRepo({ repo_url: "", branch: "", token: "" });
      setAdding(false);
      await loadRepos();
      setRepoId(created.id);
      setUseRepo(true);
    } catch (x) {
      setErr(x?.message || "Could not save the repo.");
    } finally {
      setSavingRepo(false);
    }
  }

  async function start() {
    if (!picked.size) { setErr("Pick at least one online extension."); return; }
    setBusy(true); setErr(null);
    try {
      const res = await createExtBatch({
        name: name.trim() || null,
        test_case_ids: caseIds, device_ids: [...picked],
        batch_size: Math.max(1, Math.min(Number(batchSize) || 10, 50)),
        max_cost_usd: maxCost ? Number(maxCost) : null,
        auto_compile: autoCompile,
        use_repo_compile: useRepo && !!repoId,
        repo_config_id: useRepo ? (repoId || null) : null,
      });
      onStarted(res.batch_id);
    } catch (x) {
      setErr(x?.message || "Could not start the batch.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Run ${caseIds.length} test case${caseIds.length === 1 ? "" : "s"}`} size="lg">
      <div className="space-y-4">
        <Input label="Batch name (optional)" placeholder="e.g. Regression — checkout" value={name} onChange={(e) => setName(e.target.value)} />
        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">Run on these extensions</p>
          {online.length === 0 ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">No extensions online. Open the side panel on the machines that should run.</p>
          ) : (
            <div className="grid gap-1.5 sm:grid-cols-2">
              {online.map((d) => (
                <label key={d.id} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2 text-sm dark:border-white/5 dark:bg-white/5">
                  <input type="checkbox" checked={picked.has(d.id)} onChange={() => toggle(d.id)} className="h-4 w-4 accent-brand-600" />
                  <span className="truncate text-slate-700 dark:text-slate-200">{d.label || d.id.slice(0, 12)}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Chunk size per extension" type="number" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} />
          <Input label="Cost cap (USD, optional)" icon={DollarSign} placeholder="no cap" value={maxCost} onChange={(e) => setMaxCost(e.target.value)} />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={autoCompile} onChange={(e) => setAutoCompile(e.target.checked)} className="h-4 w-4 accent-brand-600" />
          Auto-save a passing case → next run replays free
        </label>
        {/* Pre-compile from a per-project repo (fast Batch #1) */}
        <div className="rounded-xl border border-slate-100 p-3 dark:border-white/10">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={useRepo} onChange={(e) => setUseRepo(e.target.checked)} className="h-4 w-4 accent-brand-600" />
            <Cpu size={14} /> Pre-compile from repo (fast Batch #1)
          </label>
          {useRepo && (
            <div className="mt-2 space-y-2">
              {repos.length > 0 && !adding && (
                <div className="flex items-center gap-2">
                  <select value={repoId} onChange={(e) => setRepoId(e.target.value)} className={selectCls}>
                    {repos.map((r) => <option key={r.id} value={r.id}>{repoLabel(r.repo_url)}{r.branch ? ` (${r.branch})` : ""}{r.has_token ? "" : " — no token"}</option>)}
                  </select>
                  <button type="button" onClick={() => setAdding(true)} className="whitespace-nowrap text-xs font-medium text-brand-600 hover:underline dark:text-brand-300">+ Add a repo</button>
                </div>
              )}
              {(adding || repos.length === 0) && (
                <div className="space-y-2 rounded-lg bg-slate-50 p-3 dark:bg-white/5">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Add a repo for this project</p>
                  <Input label="Repo URL" placeholder="https://github.com/org/app" value={newRepo.repo_url} onChange={(e) => setNewRepo((s) => ({ ...s, repo_url: e.target.value }))} />
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input label="Branch (optional)" placeholder="main" value={newRepo.branch} onChange={(e) => setNewRepo((s) => ({ ...s, branch: e.target.value }))} />
                    <Input label="Read-only token" type="password" placeholder="ghp_… / glpat_…" value={newRepo.token} onChange={(e) => setNewRepo((s) => ({ ...s, token: e.target.value }))} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addRepo} disabled={savingRepo || !newRepo.repo_url.trim()}>
                      {savingRepo ? <Loader2 size={14} className="animate-spin" /> : <GitBranch size={14} />} Save repo
                    </Button>
                    {repos.length > 0 && <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>}
                  </div>
                </div>
              )}
              <p className="text-[0.6875rem] text-slate-400">The token is stored encrypted and reused next time (no re-typing).</p>
            </div>
          )}
        </div>

        {/* Login is taken per-case from the imported sheet (Login username/password columns)
            and used automatically when a case hits a sign-in wall — no login step here. */}
        <p className="flex items-center gap-1.5 text-[0.6875rem] text-slate-400">
          <LogIn size={12} /> Login uses the per-case credentials from your sheet automatically when a case needs sign-in.
        </p>

        {err && <p className="flex items-center gap-1.5 text-xs font-medium text-rose-600"><AlertTriangle size={14} /> {err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={start} disabled={busy || !online.length}>
            {busy ? <><Loader2 size={15} className="animate-spin" /> Starting…</> : <><Play size={15} /> Run</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// =========================================================================== //
//  RUNNING (live grid + controls)
// =========================================================================== //
function fmtElapsed(secs) {
  if (secs == null) return "—";
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// Item statuses that are done (offer Retry) vs. still in play (offer Cancel).
const TERMINAL_ITEM_STATUSES = ["failed", "error", "orphaned", "cancelled", "inconclusive"];
const ACTIVE_ITEM_STATUSES = ["queued", "requeued", "claimed", "running"];
const isBatchTerminal = (status) => ["completed", "stopped"].includes(String(status || "").toLowerCase());

// Fire a browser notification when a batch finishes. Exported so a single
// global watcher (ExtensionRunner) can notify even when the user isn't on the
// Running tab of that exact batch. No-op if notifications aren't granted.
export function notifyDone(b) {
  const label = b.name || `Batch ${String(b.id).slice(0, 8)}`;
  const extra = [
    (b.cancelled || 0) > 0 ? `${b.cancelled}⊘` : "",
    (b.inconclusive || 0) > 0 ? `${b.inconclusive}?` : "",
  ].filter(Boolean).join(" ");
  const body =
    `${label}: ${b.passed}✓ ${b.failed}✗ ${b.errored}⚠${extra ? " " + extra : ""} / ${b.total}` +
    ` · $${Number(b.cost_usd || 0).toFixed(4)}`;
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Batch finished", { body });
      return;
    }
  } catch (e) { /* ignore */ }
}

export function RunningTab({ watchBatchId, onWatch, onOpenRun }) {
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null); // ms epoch of last good poll
  const [connLost, setConnLost] = useState(false);       // last poll failed
  const [nowTs, setNowTs] = useState(() => Date.now());   // drives the "Ns ago" label
  const [itemBusy, setItemBusy] = useState(null);         // item id with a retry/cancel in flight
  const batchRef = useRef(null);                          // latest batch (for backoff decisions)
  const inFlight = useRef(false);                         // a poll is currently running

  const loadList = async () => { try { setBatches(await listExtBatches(50)); } catch (e) { /* ignore */ } };
  useEffect(() => { loadList(); }, []);

  // Fetch the watched batch + items once (used by the poller and after an action).
  const refreshNow = async () => {
    if (!watchBatchId) return;
    try {
      const [b, its] = await Promise.all([getExtBatch(watchBatchId), getExtBatchItems(watchBatchId)]);
      batchRef.current = b;
      setBatch(b); setItems(its);
      setConnLost(false); setLastUpdated(Date.now());
    } catch (e) { setConnLost(true); }
  };

  // Robust polling: never overlap in-flight requests, back off when the batch is
  // terminal or the tab is hidden, and surface connection loss instead of a silent
  // stall. Completion notifications are fired globally in ExtensionRunner.
  useEffect(() => {
    inFlight.current = false;
    batchRef.current = null;
    setConnLost(false);
    setLastUpdated(null);
    if (!watchBatchId) { setBatch(null); setItems([]); return; }

    let stopped = false;
    let handle = null;

    const nextDelay = () => {
      const b = batchRef.current;
      if (b && isBatchTerminal(b.status)) return 15000;                    // done — barely poll
      if (typeof document !== "undefined" && document.hidden) return 6000; // background tab
      return 1500;                                                         // live
    };

    const schedule = () => { if (!stopped) handle = setTimeout(tick, nextDelay()); };

    const tick = async () => {
      if (stopped) return;
      if (inFlight.current) { schedule(); return; }   // a fetch is still running — skip this tick
      inFlight.current = true;
      try {
        const [b, its] = await Promise.all([getExtBatch(watchBatchId), getExtBatchItems(watchBatchId)]);
        if (stopped) return;
        batchRef.current = b;
        setBatch(b); setItems(its);
        setConnLost(false); setLastUpdated(Date.now());
      } catch (e) {
        if (!stopped) setConnLost(true);
      } finally {
        inFlight.current = false;
        schedule();
      }
    };

    // Poll again promptly when the tab is refocused.
    const onVis = () => { if (!document.hidden && !stopped) { clearTimeout(handle); tick(); } };
    document.addEventListener("visibilitychange", onVis);

    tick();
    return () => {
      stopped = true;
      clearTimeout(handle);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [watchBatchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A 1s clock so the "updated Ns ago" label stays honest between polls.
  useEffect(() => {
    if (!watchBatchId) return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [watchBatchId]);

  const ctl = async (fn) => { try { await fn(watchBatchId); await refreshNow(); } catch (e) { /* ignore */ } };

  // Per-item Retry / Cancel, then refresh the grid so tallies stay in sync.
  async function itemAction(itemId, fn) {
    setItemBusy(itemId);
    try { await fn(watchBatchId, itemId); } catch (e) { /* ignore */ }
    finally { setItemBusy(null); }
    await refreshNow();
  }

  if (!watchBatchId) {
    return (
      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-800 dark:text-white">Batches</p>
          <DeleteAllButton
            disabled={!batches.length}
            title="Delete all batches?"
            message={`This stops any running batches (signalling their devices) and permanently deletes all ${batches.length} batch${batches.length === 1 ? "" : "es"} for your team.`}
            onDelete={deleteAllExtBatches}
            onDone={() => { onWatch(null); loadList(); }}
          />
        </div>
        {batches.length === 0 ? (
          <EmptyState icon={Play} title="No batches yet" message="Import cases, select some, and hit Run test cases." />
        ) : (
          <ul className="space-y-2">
            {batches.map((b) => (
              <li key={b.id} onClick={() => onWatch(b.id)} className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-100 bg-white p-3 hover:bg-slate-50 dark:border-white/5 dark:bg-white/5 dark:hover:bg-white/10">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{b.name || `Batch ${b.id.slice(0, 8)}`}</p>
                  <p className="text-xs text-slate-400">
                    {b.passed}✓ {b.failed}✗ {b.errored}⚠
                    {(b.cancelled || 0) > 0 ? ` ${b.cancelled}⊘` : ""}
                    {(b.inconclusive || 0) > 0 ? ` ${b.inconclusive}?` : ""}
                    {" "}/ {b.total} · ${Number(b.cost_usd || 0).toFixed(4)}
                  </p>
                </div>
                <StatusBadge status={b.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const b = batch || {};
  const passed = b.passed || 0, failed = b.failed || 0, errored = b.errored || 0;
  const cancelled = b.cancelled || 0, inconclusive = b.inconclusive || 0, orphaned = b.orphaned || 0;
  const running = b.running || 0, queued = b.queued || 0;
  // Every resolved item counts toward completion — cancelled / orphaned /
  // inconclusive included — so a fully-stopped batch reads 100%, not a phantom
  // "still running", and cancelled is never miscounted as failed.
  const done = passed + failed + errored + cancelled + inconclusive + orphaned;
  const total = b.total || 0;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const compileCost = Number(b.compile_cost || 0);
  const runCost = Number(b.cost_usd || 0);
  const secondsAgo = lastUpdated != null ? Math.max(0, Math.round((nowTs - lastUpdated) / 1000)) : null;

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button onClick={() => onWatch(null)} className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400">← All batches</button>
        <div className="flex items-center gap-2">
          {batch && (connLost ? (
            <span className="inline-flex items-center gap-1 text-[0.6875rem] font-medium text-rose-500" title="The last update request failed — retrying.">
              <WifiOff size={12} /> connection lost
            </span>
          ) : secondsAgo != null ? (
            <span className="inline-flex items-center gap-1 text-[0.6875rem] text-slate-400" title="Live updates">
              <Wifi size={12} /> updated {secondsAgo}s ago
            </span>
          ) : null)}
          {batch && <StatusBadge status={batch.status} />}
        </div>
      </div>

      {batch && (
        <>
          {/* compile progress (git pre-compile) */}
          {batch.compile_status && batch.compile_status !== "none" && (
            <div className="mb-3 rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2 text-xs dark:border-brand-500/20 dark:bg-brand-500/10">
              <Cpu size={12} className="mr-1 inline" />
              Compile phase: {batch.compile_status}
              {batch.compile_status === "running" && ` — ${batch.compiled_count}/${batch.total} pre-compiled`}
              {batch.compile_status === "done" && ` — ${batch.compiled_count}/${batch.total} run as fast replay`}
              {batch.compile_status === "failed" && " — running via live discovery"}
            </div>
          )}

          {/* counters + cost + tokens + elapsed + progress bar — each terminal
              state is its own honest tally (cancelled ≠ failed, orphaned ≠ errored) */}
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
            <span className="text-emerald-600 dark:text-emerald-400">{passed} passed</span>
            <span className="text-rose-600 dark:text-rose-400">{failed} failed</span>
            <span className="text-amber-600 dark:text-amber-400">{errored} errored</span>
            {orphaned > 0 && <span className="text-rose-700 dark:text-rose-300">{orphaned} orphaned</span>}
            {inconclusive > 0 && <span className="text-yellow-600 dark:text-yellow-400">{inconclusive} inconclusive</span>}
            {cancelled > 0 && <span className="text-slate-500 dark:text-slate-400">{cancelled} cancelled</span>}
            <span className="text-slate-500 dark:text-slate-400">{running} running · {queued} queued</span>
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400"><Clock size={11} /> {fmtElapsed(batch.elapsed_seconds)}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-white/10 dark:text-slate-300">
              <DollarSign size={11} />
              {compileCost > 0
                ? `compile $${compileCost.toFixed(4)} + run $${runCost.toFixed(4)}`
                : runCost.toFixed(4)}
            </span>
          </div>
          {(batch.input_tokens || batch.output_tokens) ? (
            <p className="mb-3 text-[0.6875rem] text-slate-400">
              {(batch.input_tokens || 0).toLocaleString()} in · {(batch.output_tokens || 0).toLocaleString()} out tokens
            </p>
          ) : null}
          <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
            <div className="h-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
          </div>

          {/* controls */}
          <div className="mb-4 flex flex-wrap gap-2">
            {batch.status === "running" && <Button size="sm" variant="outline" onClick={() => ctl(pauseExtBatch)}><Pause size={14} /> Pause</Button>}
            {batch.status === "paused" && <Button size="sm" onClick={() => ctl(resumeExtBatch)}><Play size={14} /> Resume</Button>}
            {["running", "paused"].includes(batch.status) && <Button size="sm" variant="outline" onClick={() => ctl(stopExtBatch)}><Square size={14} /> Stop</Button>}
            {batch.stalled && <Button size="sm" variant="outline" onClick={() => ctl(takeoverExtBatch)}><RotateCw size={14} /> Take over (all offline)</Button>}
            {["running", "paused"].includes(batch.status) && batch.compile_status !== "running" && (
              <Button size="sm" variant="outline" onClick={() => ctl(recompileExtBatch)}><Cpu size={14} /> Re-compile from repo</Button>
            )}
            {(batch.total || 0) > 0 && (
              <Button size="sm" variant="outline" onClick={async () => {
                try { await downloadExtBatchReport(batch.id); }
                // A console.error is invisible to the user: the spinner stopped,
                // no file arrived, and nothing said why.
                catch (e) { toastError((e && e.message) || "That report couldn't be downloaded."); }
              }}><Download size={14} /> Download report</Button>
            )}
            {/* Re-run only what needs it (dev-fixed + still failing) — see the preview. */}
            {["completed", "stopped"].includes(batch.status) && (
              <RerunBatchButton
                batch={batch}
                getPreview={getExtRerunPreview}
                rerun={rerunExtBatch}
                onDone={refreshNow}
              />
            )}
          </div>

          {/* items grid */}
          <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400 dark:border-white/10 dark:bg-white/5">
                <tr>
                  <th className="px-3 py-2">Case</th>
                  <th className="px-3 py-2">Status</th>
                  {/* What the assigned developer says about this case — a fix they've
                      claimed shows here and is what the next Re-run verifies. */}
                  <th className="px-3 py-2">Developer</th>
                  <th className="hidden px-3 py-2 sm:table-cell">Extension</th>
                  <th className="px-3 py-2">Try</th>
                  <th className="hidden px-3 py-2 sm:table-cell">Time</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const openable = !!i.run_id && typeof onOpenRun === "function";
                  const terminal = TERMINAL_ITEM_STATUSES.includes(i.status);
                  const active = ACTIVE_ITEM_STATUSES.includes(i.status);
                  return (
                    <tr
                      key={i.id}
                      onClick={openable ? () => onOpenRun(i.run_id) : undefined}
                      className={
                        "border-b border-slate-50 last:border-0 dark:border-white/5 " +
                        (openable ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5" : "")
                      }
                    >
                      <td className="max-w-[260px] px-3 py-2">
                        <p className="truncate font-medium text-slate-700 dark:text-slate-200" title={i.name}>
                          {i.name}{i.is_replay ? " ⚡" : ""}
                        </p>
                        {i.last_error && (
                          <p className="mt-0.5 truncate text-[0.6875rem] text-rose-500 dark:text-rose-400" title={i.last_error}>
                            {i.last_error}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2"><StatusBadge status={i.status} /></td>
                      <td className="px-3 py-2"><DevStatusCell item={i} /></td>
                      <td className="hidden px-3 py-2 text-slate-500 dark:text-slate-400 sm:table-cell">{i.device || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{i.attempt}</td>
                      <td className="hidden px-3 py-2 text-slate-500 dark:text-slate-400 sm:table-cell">{i.duration_seconds != null ? `${Number(i.duration_seconds).toFixed(1)}s` : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {terminal ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); itemAction(i.id, retryExtItem); }}
                            disabled={itemBusy === i.id}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                          >
                            {itemBusy === i.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />} Retry
                          </button>
                        ) : active ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); itemAction(i.id, cancelExtItem); }}
                            disabled={itemBusy === i.id}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                          >
                            {itemBusy === i.id ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />} Cancel
                          </button>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// =========================================================================== //
//  SETTINGS — git repo token + Sample-File Kit
// =========================================================================== //
export function ExtSettingsTab() {
  // Git repos + tokens are no longer added here — you add/pick one inline when
  // starting a batch (Run batch → "Pre-compile from repo"). Settings keeps just
  // the Sample-File Kit and the danger zone.
  const [samples, setSamples] = useState([]);
  const sampleRef = useRef(null);
  const [msg, setMsg] = useState(null);

  const loadSamples = async () => { try { setSamples(await listExtSampleFiles()); } catch (e) { /* ignore */ } };
  useEffect(() => { loadSamples(); }, []);

  async function onSample(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const buf = await file.arrayBuffer();
    let bin = ""; const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin);
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    try {
      await uploadExtSampleFile({ file_type: ext, filename: file.name, mime_type: file.type || null, content_b64: b64 });
      await loadSamples();
    } catch (x) { setMsg(x?.message || "Upload failed."); }
    if (sampleRef.current) sampleRef.current.value = "";
  }

  return (
    <div className="space-y-5">
      {/* Repo/token config moved into the Run batch modal — nothing to add here. */}
      <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 text-xs text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
        <span className="flex items-center gap-1.5"><GitBranch size={13} /> Add a git repo &amp; token when you start a batch (Run batch → &ldquo;Pre-compile from repo&rdquo;). It&apos;s saved encrypted and reused automatically next time.</span>
      </div>

      {/* sample kit */}
      <div className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white"><Paperclip size={15} /> Sample-File Kit</p>
          <input ref={sampleRef} type="file" onChange={onSample} className="hidden" />
          <Button size="sm" variant="outline" onClick={() => sampleRef.current?.click()}><Upload size={14} /> Add sample</Button>
        </div>
        <p className="mb-3 text-xs text-slate-400">Files auto-injected on upload steps so file-chooser cases are tested, not skipped. Upload valid samples for apps that validate content.</p>
        {samples.length === 0 ? (
          <EmptyState icon={Paperclip} title="No sample files" message="Add a pdf/jpg/png/xml/docx/csv so upload cases can run." />
        ) : (
          <ul className="grid gap-1.5 sm:grid-cols-2">
            {samples.map((s) => (
              <li key={s.id} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-2.5 text-sm dark:border-white/5 dark:bg-white/5">
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase text-slate-500 dark:bg-white/10 dark:text-slate-300">{s.file_type}</span>
                <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{s.filename}</span>
                {s.is_default ? <span className="text-[0.625rem] text-slate-400">built-in</span>
                  : <button onClick={async () => { try { await deleteExtSampleFile(s.id); loadSamples(); } catch (x) { /* ignore */ } }} className="text-slate-400 hover:text-rose-500"><Trash2 size={14} /></button>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* danger zone — wipe everything for this workspace */}
      <div className="card border-rose-200 p-5 dark:border-rose-500/20">
        <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-rose-600 dark:text-rose-300">
          <AlertTriangle size={15} /> Danger zone
        </p>
        <p className="mb-3 text-xs text-slate-400">
          Remove all imported cases, batches, and run history for your team in one go.
          Saved Tests, Devices, and repos are kept.
        </p>
        <DeleteAllButton
          label="Clear all data"
          title="Clear all extension data?"
          confirmLabel="Clear everything"
          message="This stops any running batches and permanently deletes every imported case, every batch, and all run history for your team."
          onDelete={clearAllExtData}
          onDone={(r) =>
            setMsg(
              r
                ? `Cleared — ${r.cases || 0} cases, ${r.batches || 0} batches, ${r.runs || 0} runs removed.`
                : "Cleared."
            )
          }
        />
      </div>

      {msg && <p className="text-xs text-slate-500 dark:text-slate-400">{msg}</p>}
    </div>
  );
}
